<script setup>
import {useAsyncData, useRoute} from 'nuxt/app'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import ProductItem from '~/components/partials/ProductItem'
import {useProductCategoryApi} from '~/composables/api/useProductCategoryApi'
import {useProductApi} from '~/composables/api/useProductApi'
import BreadCrumb from '~/components/common/BreadCrumb.vue'
import FilterSelector from '~/components/common/FilterSelector.vue'
import SortFieldSelector from '~/components/common/SortFieldSelector.vue'
import {getPageTitle} from '~/utils/seoUtils'
import InlineLoader from '~/components/common/InlineLoader.vue'

dayjs.extend(isBetween)

const route = useRoute()
const config = useRuntimeConfig()
const categorySlug = route.params.categorySlug
const loading = ref(false)

// Define reactive states
const selectedCollections = ref([])
const sortValues = ref({
  excerpt: '',
  updatedAt: '',
  name: '',
  price: ''
})
const searchParams = ref({
  sort: 'position',
  filters: {
    collection: ''
  }
})

// Fetch data using asyncData
const {data, error, refresh, status} = await useAsyncData(`category-${categorySlug}`, async () => {
  try {
    loading.value = true
    const productCategoryApi = useProductCategoryApi()
    const productApi = useProductApi()

    const sortParam = searchParams.value.sort
    const filterParam = searchParams.value.filters
    const [categoryResponse, fetchedProducts, fetchedCollections] = await Promise.all([productCategoryApi.getBySlug(categorySlug), productApi.getListByCategorySlug(categorySlug, sortParam, filterParam), productApi.getCollectionListByCategorySlug(categorySlug)])
    const fetchedCategory = categoryResponse

    const collectionsData = fetchedCollections.map(item => ({label: item.title, value: item.id}))

    loading.value = false
    return {
      category: fetchedCategory,
      products: fetchedProducts,
      collections: collectionsData,
      breadcrumbs: [
        {label: config.public.pageTitle, link: '/'},
        {label: fetchedCategory.title, link: `/tienda/${fetchedCategory.slug}`}
      ]
    }
  } catch (err) {
    loading.value = false
    console.error('Error fetching category', err)

    return {
      category: null,
      products: [],
      collections: [],
      breadcrumbs: []
    }
  }
})

if (!data.value || error.value) {
  throw createError({statusCode: 404, statusMessage: 'page_not_found', fatal: true})
}

const categoryTitle = computed(() => data.value?.category?.title || '')
const categoryDescription = computed(() => data.value?.category?.description || '')
const categoryImage = computed(() => data.value?.category?.image || '')

useHead({
  title: getPageTitle(categoryTitle.value, config.public.pageTitle),
  meta: [
    {
      name: 'robots',
      content: 'index, follow',
    },
    {hid: 'description', name: 'description', content: categoryDescription.value},
    {hid: 'og:type', name: 'og:type', content: 'website'},
    {
      hid: 'og:title',
      name: 'og:title',
      content: getPageTitle(categoryTitle.value, config.public.pageTitle)
    },
    {hid: 'og:description', name: 'og:description', content: categoryDescription.value},
    {hid: 'og:image', name: 'og:image', content: categoryImage.value}
  ]
})

const sortFieldChanged = (order) => {
  searchParams.value.sort = order
  refresh()
}

const collectionFieldChanged = () => {
  searchParams.value.filters['collection'] = selectedCollections.value.join(',')
  refresh()
}

function cleanSortsExcept(sortValue) {
  const fieldKey = sortValue.startsWith('-') ? sortValue.slice(1) : sortValue

  Object.keys(sortValues.value).forEach(key => {
    if (key !== fieldKey) {
      sortValues.value[key] = ''
    }
  })

  sortFieldChanged(sortValue)
}
</script>


<template>
  <div class="grid grid-cols-12 bg-[#fcfbfa]">
    <div class="col-span-12 mb-5">
      <h1 class="text-2xl lg:text-4xl font-bold italic px-4 lg:px-8 underline text-[#363330] mt-5">
        {{ categoryTitle }}
      </h1>
      <div v-if="categoryDescription" class="text-[0.9rem] lg:text-[1.2rem] font-bold italic px-4 lg:px-8 text-red-800 mt-5" v-html="categoryDescription"></div>
      <BreadCrumb class="px-4 lg:px-8" :items="data.breadcrumbs"/>
      <div class="text-xs px-4 lg:px-8 mb-4">
        <FilterSelector
          v-model="selectedCollections"
          :options="data.collections"
          title="Filtro"
          @update:model-value="collectionFieldChanged"
        />
      </div>
      <div class="flex gap-4 px-4 lg:px-8 my-3 text-[#363330]">
        <div class="bg-[#363330] text-[#fcfbfa] py-1 px-2 text-xs">Orden</div>
        <SortFieldSelector title="Recientes" field="updatedAt" v-model="sortValues.updatedAt"
                           @update:model-value="cleanSortsExcept"/>
        <SortFieldSelector title="Nombre" field="title" v-model="sortValues.title"
                           @update:model-value="cleanSortsExcept"/>
        <SortFieldSelector title="Precio" field="price" v-model="sortValues.price"
                           @update:model-value="cleanSortsExcept"/>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-12 px-4 lg:px-8 gap-4 lg:gap-10 my-5">
    <InlineLoader v-show="loading" class="col-span-12" title="Cargando productos"/>
    <ProductItem v-for="product in data.products" :product="product" :key="product.id"/>
  </div>
</template>
